import { useAuth } from "@clerk/clerk-react";
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { BrandsContext } from "contexts/Brands";
import { MAX_SELECT_BRANDS, NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  BentoBrand,
  MetadataType,
  SearchParams,
  Sort,
} from "schemas/dashboard";

import CategoryFilter from "features/Influencer/Discover/Results/CategoryFilter";
import MobileCategoryFilterDialog from "features/Influencer/Discover/Results/CategoryFilter/mobile";
import DiscoverFilter from "features/Influencer/Discover/Results/DiscoverFilter";
import DiscoverSearchBar from "features/Influencer/Discover/Search";
import { arrayQuery, fetcherAuth } from "utils/api";
import {
  convertFilterParamsToQuery,
  getCategoryIdParams,
  getMinAndMaxAge,
  getMinAndMaxInstagram,
} from "utils/discoverSearch";
import { trackEvent } from "utils/tracking";

import Brands from "./Brands";

const RESULTS_PER_PAGE = 20;

export default function BrandSearch({
  repitching = false,
}: {
  repitching?: boolean;
}) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const {
    selectAll,
    selectedBrandIds,
    setSelectAll,
    setSelectedBrandIds,
    setCurrentSearchParams,
    searchUrlParams,
    setSearchUrlParams,
  } = useContext(NewCampaignContext);
  const { topLevelCategories, categoriesById, categoriesLoading } = useContext(
    BentoCategoriesContext,
  );
  const { setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  const { discoverFilterParams, selectedMetadataTags } =
    useContext(BrandsContext);
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const {
    selectedSort,
    selectedCategoryIds,
    selectedCompanyTypes,
    selectedContactTypes,
    selectedInstagramFollowings,
    selectedLocations,
    selectedGeopoints,
    selectedCompensationTypes,
    selectedTargetAges,
    selectedTargetGenders,
  } = { ...discoverFilterParams };

  const [brands, setBrands] = useState<BentoBrand[]>([]);
  const abortController = useRef<AbortController | undefined>(undefined);
  const [cursor, setCursor] = useState<number[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [openMobileCategoryFilter, setOpenMobileCategoryFilter] =
    useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  const [ref, isBrandsRefVisible] = useInView({
    rootMargin: "0px 0px",
  });
  const brandQuery = searchParams.get(SearchParams.QUERY_DISCOVER);

  const [brandsLoading, setBrandsLoading] = useState<boolean>(true);
  const [fetchMoreLoading, setFetchMoreLoading] = useState<boolean>(false);
  const [hitsLimit, setHitsLimit] = useState<boolean>(false);
  const headRef = useRef<HTMLDivElement>(null);

  const fetchBrands = async (
    searchQuery: string | null,
    fetchCursor: number[] | null,
    useFetchMoreLoading?: boolean,
    appendToList?: boolean,
  ) => {
    if (!currentOrg) {
      setBrandsLoading(false);
      return;
    }

    if (abortController?.current) {
      abortController.current?.abort();
    }

    try {
      abortController.current = new AbortController();

      let query = searchQuery || "";
      if (selectedMetadataTags?.length > 0) {
        const clickedTags = selectedMetadataTags?.filter(
          (x) => x.type === MetadataType.tags,
        );
        query += clickedTags?.map((x) => x.value);
      }

      const encodedQuery = encodeURIComponent(query || "");

      let url = `/api/organization/${currentOrg?.id}/bento-brands?query=${encodedQuery}&size=${RESULTS_PER_PAGE}&contacts_required=true`;
      if (selectedSort) {
        url += `&sort=${selectedSort?.key}`;
      } else {
        url += `&sort=${Sort.Recommended}`;
      }

      if (selectedCategoryIds?.length > 0) {
        const categoryQueries = getCategoryIdParams(
          topLevelCategories,
          categoriesById,
          selectedCategoryIds,
        );
        url += `${arrayQuery(categoryQueries, "category_ids")}`;
      }

      if (selectedCompanyTypes?.length > 0) {
        url += `${arrayQuery(
          selectedCompanyTypes?.map((x) => x.key),
          "company_types",
        )}`;
      }

      if (selectedContactTypes?.length > 0) {
        url += `${arrayQuery(
          selectedContactTypes?.map((x) => x.key),
          "contact_types",
        )}`;
      }

      if (selectedCompensationTypes?.length > 0) {
        url += `${arrayQuery(
          selectedCompensationTypes?.map((x) => x.key),
          "compensation_types",
        )}`;
      }

      if (selectedTargetGenders?.length > 0) {
        url += `${arrayQuery(
          selectedTargetGenders?.map((x) => x.key),
          "target_genders",
        )}`;
      }

      if (selectedLocations?.length > 0) {
        url += `${arrayQuery(
          selectedLocations?.map((x) => x.key),
          "locations",
        )}`;
      }

      if (selectedGeopoints?.length > 0) {
        url += `${arrayQuery(
          selectedGeopoints?.map((x) => x.key),
          "geopoints",
        )}`;
      }

      if (selectedInstagramFollowings?.length > 0) {
        const followers = getMinAndMaxInstagram(selectedInstagramFollowings);
        if (followers.min_instagram_followers) {
          url += `&min_instagram_followers=${followers.min_instagram_followers}`;
        }
        if (followers.max_instagram_followers) {
          url += `&max_instagram_followers=${followers.max_instagram_followers}`;
        }
      }
      if (selectedTargetAges?.length > 0) {
        const ages = getMinAndMaxAge(selectedTargetAges);
        if (ages.min_target_age) {
          url += `&min_target_age=${ages.min_target_age}`;
        }
        if (ages.max_target_age) {
          url += `&max_target_age=${ages.max_target_age}`;
        }
      }
      if (fetchCursor) {
        url += `&cursor=${JSON.stringify(fetchCursor)}`;
      }

      if (repitching) {
        url += "&is_repitching=true";
      }

      if (useFetchMoreLoading) {
        setFetchMoreLoading(true);
      } else {
        setBrandsLoading(true);
      }
      const res = await fetcherAuth(
        getToken,
        url,
        "GET",
        {},
        {},
        false,
        false,
        true,
        abortController.current.signal,
      );

      if (res.bentoBrands.length < RESULTS_PER_PAGE) {
        setHitsLimit(true);
      } else {
        setHitsLimit(false);
      }

      if (appendToList) {
        setBrands((prevBrands: BentoBrand[]) => {
          const brandSet = new Set(prevBrands.map((brand) => brand.id));
          const newBrands = [...prevBrands];

          res.bentoBrands.forEach((brand: BentoBrand) => {
            if (!brandSet.has(brand.id)) {
              brandSet.add(brand.id);
              if (newBrands) {
                newBrands.push(brand);
              }
            }
          });
          return newBrands;
        });
      } else {
        setBrands([...res.bentoBrands]);
      }
      setCursor(res.cursor);
      setTotal(res.totalResults);
    } catch (error) {
      if (error?.message?.includes("aborted")) {
        return;
      }
      setAlert(
        error?.message ||
          "Something went wrong when fetching brands, please reload and try again",
        "error",
      );
    } finally {
      setBrandsLoading(false);
      setFetchMoreLoading(false);
    }
  };

  const handleSearch = async () => {
    scrollToTop();
    setBrandsLoading(true);
    setCursor(null);
    setHitsLimit(false);
    fetchBrands(brandQuery, null, false, false);
  };

  const scrollToTop = () => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const isElementInView = () => {
      const rect = headRef?.current?.getBoundingClientRect();
      if (!rect) {
        return false;
      }
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      );
    };

    if (headRef.current && !isElementInView()) {
      headRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(discoverFilterParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    selectedMetadataTags?.length,
    brandQuery,
    currentOrg?.id,
  ]);

  useEffect(
    () => () => {
      if (abortController.current) {
        abortController.current?.abort();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (searchUrlParams && !location.search) {
      navigate(`${searchUrlParams}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isBrandsRefVisible) {
      if (!hitsLimit && !brandsLoading) {
        fetchBrands(brandQuery, cursor, true, true);
        trackEvent("Campaigns Brand Search Page Scrolled To Bottom", {
          keyword: brandQuery,
          categoryIds: selectedCategoryIds,
          "Company Type": selectedCompanyTypes,
          sort: selectedSort,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrandsRefVisible, brandsLoading]);

  const numberSelected = selectAll
    ? Math.min(total, MAX_SELECT_BRANDS)
    : selectedBrandIds.size;

  return (
    <>
      <DialogContent>
        <Grid container>
          <div ref={resultsRef} />

          <Grid item xs={12}>
            <DiscoverSearchBar alwaysShow />

            <DiscoverFilter newCampaignView />
          </Grid>
          {!isMobileScreen && (
            <Grid item md={3}>
              <CategoryFilter topLevelCategories={topLevelCategories} />
            </Grid>
          )}
          <Grid item xs={12} md={9}>
            <Brands
              brands={brands}
              brandsLoading={brandsLoading || categoriesLoading}
              fetchMoreLoading={fetchMoreLoading}
              ref={ref}
              numberSelected={numberSelected}
              total={total}
              isRepitching={repitching}
            />
          </Grid>
        </Grid>

        {isMobileScreen && (
          <MobileCategoryFilterDialog
            topLevelCategories={topLevelCategories}
            openMobileCategoryFilter={openMobileCategoryFilter}
            setOpenMobileCategoryFilter={setOpenMobileCategoryFilter}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          onClick={() => {
            navigate("../type");
            setSelectAll(false);
            setSelectedBrandIds(new Set());
          }}
          disableElevation
        >
          Back
        </Button>
        <Button
          disabled={!selectAll && selectedBrandIds.size === 0}
          variant="contained"
          disableElevation
          onClick={() => {
            const converted = convertFilterParamsToQuery(
              topLevelCategories,
              categoriesById,
              discoverFilterParams,
              selectedMetadataTags,
              brandQuery || "",
            );
            setCurrentSearchParams(converted);
            setSearchUrlParams(location.search);
            trackEvent("New Campaign Dialog Selected Brand Search Brands", {
              "Select All": selectAll,
              "Number Of Brands Selected": selectedBrandIds.size,
              "Search Params": location.search,
            });
            navigate("../details");
          }}
        >
          Next
        </Button>
      </DialogActions>
    </>
  );
}
